import type { ThemeConfig } from 'antd';

/**
 * One place for the visual language: colour, radius, density, and the handful
 * of component overrides that antd's defaults do not get right for a console
 * whose main job is dense tables of infrastructure state.
 *
 * Everything a page needs comes from here or from antd's tokens, so a page
 * never hard-codes a hex value.
 */

export const brand = {
  primary: '#2563eb',
  primaryHover: '#1d4ed8',
  success: '#059669',
  warning: '#d97706',
  danger: '#dc2626',
  ink: '#0f172a',
  body: '#475569',
  muted: '#64748b',
  faint: '#94a3b8',
  line: '#e8ebf0',
  lineSoft: '#f1f3f7',
  surface: '#ffffff',
  canvas: '#f5f7fa',
  raised: '#fafbfd',
} as const;

export const mono =
  "'SF Mono', ui-monospace, SFMono-Regular, 'JetBrains Mono', Menlo, Consolas, monospace";

export const sans =
  "-apple-system, BlinkMacSystemFont, 'Inter', 'Segoe UI', Roboto, 'Helvetica Neue', Arial, 'Noto Sans JP', 'Hiragino Sans', sans-serif";

/** Shadows: two levels only — a resting surface and a floating one. */
export const shadow = {
  card: '0 1px 2px rgba(15, 23, 42, 0.04)',
  raised: '0 6px 24px -8px rgba(15, 23, 42, 0.18), 0 2px 6px rgba(15, 23, 42, 0.06)',
} as const;

const theme: ThemeConfig = {
  token: {
    colorPrimary: brand.primary,
    colorSuccess: brand.success,
    colorWarning: brand.warning,
    colorError: brand.danger,
    colorInfo: brand.primary,
    colorTextBase: brand.ink,
    colorText: brand.ink,
    colorTextSecondary: brand.body,
    colorTextTertiary: brand.muted,
    colorTextQuaternary: brand.faint,
    colorBgLayout: brand.canvas,
    colorBorder: brand.line,
    colorBorderSecondary: brand.lineSoft,
    borderRadius: 8,
    borderRadiusLG: 12,
    borderRadiusSM: 6,
    fontFamily: sans,
    fontSize: 14,
    controlHeight: 36,
    controlHeightLG: 40,
    controlHeightSM: 28,
    lineWidth: 1,
    wireframe: false,
    boxShadowSecondary: shadow.raised,
  },
  components: {
    Layout: {
      headerBg: brand.surface,
      headerHeight: 56,
      headerPadding: '0 20px',
      bodyBg: brand.canvas,
    },
    Menu: {
      itemBorderRadius: 8,
      itemHeight: 36,
      horizontalItemSelectedColor: brand.primary,
      itemSelectedBg: '#eef3ff',
      itemSelectedColor: brand.primary,
      activeBarBorderWidth: 0,
      itemMarginInline: 4,
    },
    Card: {
      borderRadiusLG: 12,
      paddingLG: 20,
      headerFontSize: 15,
      headerHeight: 52,
      colorBorderSecondary: brand.line,
    },
    Table: {
      headerBg: brand.raised,
      headerColor: brand.muted,
      headerSplitColor: 'transparent',
      borderColor: brand.lineSoft,
      rowHoverBg: '#f7f9fe',
      cellPaddingBlock: 13,
      cellPaddingInline: 16,
      headerBorderRadius: 0,
      footerBg: 'transparent',
    },
    Button: {
      fontWeight: 500,
      primaryShadow: 'none',
      defaultShadow: 'none',
      dangerShadow: 'none',
      paddingInline: 14,
    },
    Input: { paddingBlock: 5 },
    InputNumber: { paddingBlock: 3 },
    Select: { optionSelectedBg: '#eef3ff' },
    Modal: {
      borderRadiusLG: 14,
      headerBg: 'transparent',
      titleFontSize: 17,
      paddingContentHorizontalLG: 24,
    },
    Tabs: { horizontalItemPadding: '10px 0', horizontalItemGutter: 24 },
    Tag: { borderRadiusSM: 6, defaultBg: brand.lineSoft, defaultColor: brand.body },
    Descriptions: { labelColor: brand.muted, titleMarginBottom: 12 },
    Alert: { borderRadiusLG: 10, withDescriptionPadding: '14px 16px' },
    Segmented: { itemSelectedBg: brand.surface, trackBg: brand.lineSoft, borderRadius: 8 },
    Switch: { trackHeight: 20, trackMinWidth: 38 },
    Tooltip: { borderRadius: 8 },
    Form: {
      labelColor: brand.body,
      labelFontSize: 13,
      itemMarginBottom: 20,
      verticalLabelPadding: '0 0 6px',
    },
    Statistic: { titleFontSize: 13, contentFontSize: 26 },
    Empty: { colorTextDescription: brand.faint },
  },
};

export default theme;
